<template>
  <div align="Left">
    <md-card id="patients" align="Left">
      <md-card-header class="md-card-header-icon md-card-header-green">
        <div class="card-icon">
          <i class="material-icons">people</i>
        </div>
        <div id="patients_title" class="title">
          <h4>Patients List</h4>

          <md-button id="patient_csv_import" class="md-success md-sm" @click="triggerCSVFileInput">
            <i class="fas fa-upload" />
            Import CSV
          </md-button>
          <input type="file" ref="fileInput" accept=".csv" style="display: none" @change="handleCSVImport" />

          <md-dialog id="#patients_import_modal" style="min-height: 250px" :md-active.sync="showUploadFileResultModal">
            <md-dialog-title>CSV Summary</md-dialog-title>
            <md-dialog-content>
              <loader :is-visible="isLoadingCSV" />
              <template v-if="!isLoadingCSV">
                <div v-if="showOrganizationSelector && CSVSeparatorChar"
                  style="display: flex; flex-direction: row; align-items: center;  gap: 0.75rem; margin-bottom: 1rem;">
                  <md-field :class="[
                    { 'md-valid': touched.organization },
                    { 'md-form-group': true },
                    { 'md-error': errors.has('organization') }
                  ]">
                    <md-icon>corporate_fare</md-icon>
                    <label for="specialization">Select Organization to import patients to</label>
                    <md-select v-model="orgIdControl" name="organization" style="margin-left: 12px;width: inherit"
                      v-validate="'required'">
                      <md-option v-for="item in orgIds" :key="item.OrgId" :value="item.OrgId">
                        {{ item.OrgName }}
                      </md-option>
                    </md-select>
                    <slide-y-down-transition>
                      <md-icon v-show="errors.has('organization')" style="padding: 0" class="alert">close</md-icon>
                    </slide-y-down-transition>
                  </md-field>
                </div>
                <div style="display: flex;justify-content: space-between;">
                  <div>{{ totalRows || 0 }} Total rows</div>
                  <div>{{ currentCSVDataJson.length || 0 }} Patient(s) Found</div>
                </div>
                <div style="display: flex; flex-direction: row; align-items: center; gap: 32px; padding: 18px 0;"
                  v-if="!CSVSeparatorChar">
                  <p style="color: #ed7632">The CSV file must be read using either a comma (,) or a semicolon (;) as the
                    separator.
                    <strong>Import a new CSV </strong>
                  </p>
                </div>

                <md-table :value="currentCSVDataJson" class="table-shopping table-striped table-hover"
                  style="max-height: 300px;overflow-x: scroll;">
                  <md-table-row>
                    <md-table-empty-state :md-description="`No valid patients found in this CSV`"
                      md-label="No patients found" />
                  </md-table-row>
                  <md-table-row slot="md-table-row" slot-scope="{ item }">
                    <md-table-cell />
                    <md-table-cell md-label="Patient Name">{{ item["Patient First Name"] }} {{ item["Patient Last Name"]
                    }}
                    </md-table-cell>
                    <md-table-cell md-label="Gender">{{ item["Patient Sex"] && item["Patient Sex"] != 'None Entered' ?
                      item["Patient Sex"] : '-' }}</md-table-cell>
                    <md-table-cell md-label="Email">{{ item["Patient Email"] || '-' }}</md-table-cell>
                    <md-table-cell md-label="Phone">{{ item["Patient Cell Phone"] || '-' }}</md-table-cell>
                    <md-table-cell md-label="Date of Birth">{{ item["Patient DOB"] || '-' }}</md-table-cell>
                    <md-table-cell />
                  </md-table-row>
                </md-table>
              </template>

            </md-dialog-content>
            <md-dialog-actions style="justify-content: center;margin-bottom: 1rem;padding: 0;">
              <md-button style="width: 125px;background-color: #fff !important;box-shadow: none;color: #000 !important;"
                @click="closeUploadFileResultModal">Cancel</md-button>
              <md-button :disabled="!currentCSVDataJson.length || isImportingFile" style="width: 125px;"
                class="md-primary" v-if="CSVSeparatorChar" @click="uploadFile">
                <span :class="[{ 'importing-file': isImportingFile }]">
                  {{ isImportingFile ? 'Importing...' : 'Import' }}
                </span>
              </md-button>
            </md-dialog-actions>
          </md-dialog>
        </div>

        <md-dialog v-if="feedbackData" id="feedback-modal" style="min-height: 250px"
          :md-active.sync="showImportFeedbackModal">
          <md-dialog-title>CSV Import Summary</md-dialog-title>
          <md-dialog-content>

            <div style="margin-bottom: 1rem;">
              <div>
                {{ feedbackData.createdCount }} patient(s) were created successfully
              </div>
            </div>

            <v-expansion-panels v-if="(feedbackData.duplicatedNotPatient && feedbackData.duplicatedNotPatient.length) ||
              (feedbackData.duplicatedPatientInPlatform && feedbackData.duplicatedPatientInPlatform.length) ||
              (feedbackData.duplicatedPatientInFile && feedbackData.duplicatedPatientInFile.length)">
              <v-expansion-panel v-if="feedbackData.duplicatedNotPatient && feedbackData.duplicatedNotPatient.length">
                <v-expansion-panel-header>
                  <div style="display: flex;justify-content: flex-start; align-items: center;">
                    <b style="color: red;margin-right: 5px;">ERROR</b>
                    <span style="font-size: 12px;">
                      The email(s) uploaded below are already saved in the platform as business roles, not patient
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ul style="margin-left: 1rem;">
                    <li style="font-size: 14px;" v-for="(email, index) in feedbackData.duplicatedNotPatient"
                      :key="index">
                      {{ email }}
                    </li>
                  </ul>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel
                v-if="feedbackData.duplicatedPatientInPlatform && feedbackData.duplicatedPatientInPlatform.length">
                <v-expansion-panel-header>
                  <div style="display: flex;justify-content: flex-start; align-items: center;">
                    <b style="color: orange;margin-right: 5px;">WARNING</b>
                    <span style="font-size: 12px;">
                      Patient(s) already with records in the system. The uploaded data will replace demographic patients
                      data
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ul style="margin-left: 1rem;">
                    <li style="font-size: 14px;" v-for="(email, index) in feedbackData.duplicatedPatientInPlatform"
                      :key="index">
                      {{ email }}
                    </li>
                  </ul>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <v-expansion-panel
                v-if="feedbackData.duplicatedPatientInFile && feedbackData.duplicatedPatientInFile.length">
                <v-expansion-panel-header>
                  <div style="display: flex;justify-content: flex-start; align-items: center;">
                    <b style="color: orange;margin-right: 5px;">WARNING</b>
                    <span style="font-size: 12px;">
                      Duplicate patient(s) records detected for emails below. Only the first entry will be processed for
                      each
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ul style="margin-left: 1rem;">
                    <li style="font-size: 14px;" v-for="(email, index) in feedbackData.duplicatedPatientInFile"
                      :key="index">
                      {{ email }}
                    </li>
                  </ul>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </md-dialog-content>
          <md-dialog-actions style="justify-content: center;margin-bottom: 1rem;padding: 0;">
            <md-button class="md-primary" style="width: 110px;"
              @click="showImportFeedbackModal = false; feedbackData = null">Close</md-button>
          </md-dialog-actions>
        </md-dialog>

        <div class="md-layout md-gutter md-layout-item md-size-100 md-alignment-center-left" style="padding: 0;">
          <div class="md-layout md-gutter md-layout-item md-size-20">
            <md-field class="md-sm">
              <label>Search</label>
              <md-input v-model="searchQuery" @keyup.enter="handleSearch" />
            </md-field>
          </div>
          <div class="md-layout md-gutter md-layout-item md-size-20" v-if="organizations.length > 2">
            <md-field class="md-sm">
              <label for="currentType">Organization</label>
              <md-select v-model="currentOrganization" name="organization">
                <md-option v-for="(type, index) in organizations" :key="index" :value="type.value">
                  {{ type.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout md-gutter md-layout-item md-size-20">
            <md-button class="md-icon-button md-primary" @click="handleSearch">
              <md-icon>search</md-icon>
            </md-button>
          </div>
          <div class="md-layout md-gutter md-layout-item md-alignment-center-right" :class="[
            { 'md-size-40': organizations.length > 2 },
            { 'md-size-60': organizations.length <= 2 }
          ]">
            <md-button :disabled="isLoading" class="md-icon-button" @click="handleRefresh">
              <md-icon>refresh</md-icon>
              <md-tooltip md-direction="bottom">Refresh list</md-tooltip>
            </md-button>
          </div>
        </div>
      </md-card-header>

      <md-card-content style="padding-top: 0px" v-if="!isLoading">
        <md-table :value="tableData" class="table-shopping table-striped table-hover" @md-selected="getSelected"
          @click.native="labelSort($event, false)">
          <md-table-row>
            <md-table-empty-state :md-description="`No patients found for this time frame or search criteria`"
              md-label="No patients found" />
          </md-table-row>
          <md-table-row slot="md-table-row" slot-scope="{ item }" md-selectable="single">
            <md-table-cell md-label="First Name">
              <span>{{ item.person.firstName }}</span>
            </md-table-cell>

            <md-table-cell md-label="Last Name">
              {{ item.person.lastName }}
            </md-table-cell>

            <md-table-cell md-label="Gender">{{ item.person.gender || '-' }}</md-table-cell>
            <md-table-cell md-label="Email">{{ item.person.email || '-' }}</md-table-cell>
            <md-table-cell md-label="Phone">{{ item.person.cellNumber || '-' }}</md-table-cell>
          </md-table-row>
        </md-table>
        <hr />
        <div style="display: flex; align-items: center">
          <div class="pagination-container" style="flex: 1; justify-content: start; height: 50%">
            <a :disabled="offset === 0" class="page-link" style="color: #999" aria-label="Previous" @click="prevPage">
              <i class="fas fa-angle-double-left" />
            </a>
            <span class="page-link"
              style="background-color: #4caf50; color: white; padding: 5px 10px; border-radius: 16px">{{
                currentPage }}</span>
            <a class="page-link" aria-label="Next" style="color: #999" @click="nextPage">
              <i class="fas fa-angle-double-right" />
            </a>
          </div>
          <div class="table table-stats" style="display: flex; justify-content: center; align-items: start; flex: 2">
            <div class="text-right">
              <p class="card-category">
                Showing from
                <b><u>{{ startPatient }}</u></b>
                to
                <b><u>{{ endPatient }}</u></b>. Total of
                <b><u>{{ totalReaderPatients }}</u></b>
                patients.
              </p>
            </div>
          </div>
          <md-field style="flex: 1; margin-top: 1.5rem">
            <label for="pages">Per page</label>
            <md-select v-model="pagination.perPage" name="pages" @change="updateLimit">
              <md-option v-for="item in pagination.perPageOptions" :key="item" :value="item">
                {{ item }}
              </md-option>
            </md-select>
          </md-field>
        </div>
      </md-card-content>
    </md-card>

    <loader :is-visible="isLoading" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import loader from "@/components/GlobeChek/loader.vue"
import Swal from "sweetalert2"
import { SlideYDownTransition } from "vue2-transitions"

export default {
  components: {
    loader,
    SlideYDownTransition
  },
  data() {
    return {
      isLoadingCSV: false,
      CSVSeparatorChar: ',',
      showUploadFileResultModal: false,
      currentCSVDataJson: [],
      totalRows: 0,
      currentCSVFormData: null,
      currentCSV: null,
      tableData: [],
      searchQuery: "",
      searchedData: [],
      isLoading: false,
      orgIdControl: null,
      orgIds: [],
      orgNamesObject: Object,
      showOrganizationSelector: false,
      showImportFeedbackModal: false,
      feedbackData: null,
      isImportingFile: false,
      touched: {
        organization: false
      },
      offset: 0,
      limit: 50,
      pagination: {
        perPage: 50,
        perPageOptions: [10, 20, 50, 100, 200]
      },
      columnAliases: {
        'Patient First Name': ['first', 'first name', 'surname', 'firstname'],
        'Patient Last Name': ['last', 'last name', 'lastname'],
        'Patient DOB': ['dob', 'date of birth', 'dateofbirth', 'Patient Date of Birth', 'DateOfBirth'],
        'Patient Sex': ['sex', 'gender', 'patient gender'],
        'Patient Email': ['email', 'patient e-mail', 'e-mail'],
        'Patient Cell Phone': ['phone', 'cellular', 'cell phone', 'home phone', 'homephone', 'Patient Home Phone', 'Patient Work Phone', 'work phone', 'other phone', 'workPhone', 'otherPhone'],
        'Patient Address': ['address', 'address 1'],
        'Patient City': ['city'],
        'Patient State/Prov': ['state', 'state code'],
        'Patient Zip Code': ['zip', 'zipcode', 'zip code']
      },
      currentOrganization: JSON.parse(sessionStorage.getItem("organizations")).length === 1 ? JSON.parse(sessionStorage.getItem("organizations"))[0].orgId : (localStorage.getItem("currentOrgSelectedForFilters") || "all"),
      organizations: [
        { label: 'All', value: 'all' },
        ...JSON.parse(sessionStorage.getItem('organizations')).map(i => {
          return {
            label: i.orgName,
            value: i.orgId
          }
        }),
      ],
    }
  },
  computed: {
    ...mapGetters(["patientsList", "totalReaderPatients"]),
    filteredPatients() {
      if (!this.tableData) return []
      return this.tableData.filter((patient) =>
        Object.values(patient.person).some(
          (value) => typeof value === "string" && value.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      )
    },
    currentPage() {
      return Math.floor(this.offset / this.limit) + 1
    },
    hasMoreData() {
      return this.offset + this.limit < this.totalReaderPatients
    },
    startPatient() {
      return this.offset + 1
    },
    endPatient() {
      return Math.min(this.offset + this.limit, this.totalReaderPatients)
    }
  },
  watch: {
    "pagination.perPage"(newLimit) {
      this.limit = newLimit
      this.offset = 0
      this.fetchPatients({ search: this.searchQuery, orgId: this.currentOrganization })
    }
    // searchQuery(value) {
    //   if (value !== "") {
    //     this.fetchPatients({ search: value });
    //   } else {
    //     this.fetchPatients();
    //   }
    // },
  },
  mounted() {
    this.isLoading = true
    this.fetchPatients({ orgId: this.currentOrganization })
  },
  methods: {
    ...mapActions(["listPatients"]),
    handleRefresh() {
      this.fetchPatients({ search: this.searchQuery, orgId: this.currentOrganization })
    },
    async fetchPatients({ search = null, orgId = null } = {}) {
      this.isLoading = true
      try {
        const response = await this.listPatients({
          offset: this.offset,
          limit: this.pagination.perPage,
          search: search,
          orgId: orgId != 'all' ? orgId : null
        })
        this.tableData = response.patients
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    updateLimit() {
      this.offset = 0
      this.fetchPatients({ search: this.searchQuery, orgId: this.currentOrganization })
    },
    handleSearch() {
      this.offset = 0
      localStorage.setItem('currentOrgSelectedForFilters', this.currentOrganization)
      this.fetchPatients({ search: this.searchQuery, orgId: this.currentOrganization })
    },
    prevPage() {
      if (this.offset > 0) {
        this.offset -= this.limit
        this.fetchPatients({ search: this.searchQuery, orgId: this.currentOrganization })
      }
    },
    nextPage() {
      if (this.hasMoreData) {
        this.offset += this.limit
        this.fetchPatients({ search: this.searchQuery, orgId: this.currentOrganization })
      }
    },
    getSelected(item) {
      localStorage.setItem("patientInfo", JSON.stringify(item))
      this.$router.push({ path: `${sessionStorage.getItem('portalRoute')}/patient`, params: item })
    },
    labelSort(type, menu) {
      let sort = menu ? type : event.target.textContent.trim()
      this.sortBy = sort
      if (sort == this.sortBy) {
        this.orderAsc = !this.orderAsc
      } else {
        this.orderAsc = true
      }
      switch (sort.toLowerCase()) {
        case "first name":
          this.sortData("firstName")
          break
        case "last name":
          this.sortData("lastName")
          break
        case "gender":
          this.sortData("gender")
          break
        case "email":
          this.sortData("email")
          break
        case "phone":
          this.sortData("cellNumber")
          break
        default:
          return null
      }
    },
    sortData(sortValue) {
      const sortedData = this.tableData.sort((a, b) => {
        if (this.orderAsc) {
          return a.person[sortValue] > b.person[sortValue] ? 1 : -1
        } else {
          return a.person[sortValue] < b.person[sortValue] ? 1 : -1
        }
      })
      this.tableData = sortedData
    },
    triggerCSVFileInput() {
      this.$refs.fileInput.click();
    },
    detectDelimiter(csvContent) {
      const firstLine = csvContent.split('\n')[0];
      const commaCount = (firstLine.match(/,/g) || []).length;
      const semicolonCount = (firstLine.match(/;/g) || []).length;
      if (commaCount === 0 && semicolonCount === 0) {
        return null;
      }

      if (commaCount > semicolonCount) {
        return ',';
      } else if (semicolonCount > commaCount) {
        return ';';
      } else {
        return null;
      }
    },
    async handleCSVImport() {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader()
        reader.onload = async (e) => {
          this.currentCSV = e.target.result;
          this.CSVSeparatorChar = this.detectDelimiter(this.currentCSV);
          this.currentCSVFormData = new FormData();
          this.currentCSVDataJson = {};
          if (this.CSVSeparatorChar) {
            await this.convertCsvToJson(this.currentCSV);
            this.currentCSVFormData.append("file", file);
          }
          await this.fetchOrganizations();
          this.touched.organization = false;
          this.orgIdControl = null;
          this.openUploadFileResultModal();
          this.$refs.fileInput.value = "";
        }
        reader.readAsText(file)
      }
    },
    normalizeColumnName(name) {
      return name.trim().toLowerCase().replace(/[-_ ]/g, '');
    },

    buildColumnMapping(headers) {
      const mapping = {};
      const normalizedHeaders = {};

      headers.forEach(header => {
        const normalized = this.normalizeColumnName(header);
        normalizedHeaders[normalized] = header;
      });

      for (const [standardName, aliases] of Object.entries(this.columnAliases)) {
        const normalizedStandard = this.normalizeColumnName(standardName);
        if (normalizedHeaders[normalizedStandard]) {
          mapping[standardName] = normalizedHeaders[normalizedStandard];
          continue;
        }

        for (const alias of aliases) {
          const normalizedAlias = this.normalizeColumnName(alias);
          if (normalizedHeaders[normalizedAlias]) {
            mapping[standardName] = normalizedHeaders[normalizedAlias];
            break;
          }
        }
      }
      return mapping;
    },
    async convertCsvToJson() {
      this.isLoadingCSV = true;
      window.Papa.parse(this.currentCSV, {
        header: true,
        complete: (parsed) => {
          const lines = parsed.data;
          const headers = Object.keys(parsed.data[0]);

          const columnMapping = this.buildColumnMapping(headers);

          const requiredColumns = ['Patient First Name', 'Patient Last Name', 'Patient DOB', 'Patient Email'];
          const missingColumns = requiredColumns.filter(col => !columnMapping[col]);

          if (missingColumns.length > 0) {
            this.isLoadingCSV = false;
            Swal.fire({
              title: "Error",
              text: `Missing required columns: ${missingColumns.join(', ')}`,
              type: "error",
              confirmButtonClass: "md-button md-info",
              buttonsStyling: false
            }).then(() => {
              this.closeUploadFileResultModal();
            });
            return;
          }

          this.currentCSVDataJson = lines.map(obj => {
            const standardizedObj = {};
            for (const [standardName, csvHeader] of Object.entries(columnMapping)) {
              standardizedObj[standardName] = obj[csvHeader];
            }
            return standardizedObj;
          });

          this.totalRows = this.currentCSVDataJson.length;
          this.currentCSVDataJson = this.currentCSVDataJson.filter(i => !!i['Patient Email']);
          this.isLoadingCSV = false;
        }
      });
    },
    openUploadFileResultModal() {
      this.showUploadFileResultModal = true
    },
    closeUploadFileResultModal() {
      this.showUploadFileResultModal = false
    },
    async uploadFile() {
      try {
        this.touched.organization = true;
        const valid = await this.$validator.validateAll();
        if (!valid) return;
        this.isImportingFile = true;
        const token = sessionStorage.getItem("token");
        if (!token) {
          console.error("token not found");
          return;
        }

        const csvContent = this.currentCSV

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const formData = new FormData();
        formData.append('file', blob, 'patients.csv');
        formData.append('org_id', this.orgIdControl);

        const url = `${process.env.VUE_APP_URL}/patient/registration/csv`;
        const config = {
          headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${token}`
          }
        };
        const importResponse = await window.axios.post(url, formData, config);
        this.closeUploadFileResultModal();
        this.fetchPatients({ search: this.searchQuery, orgId: this.currentOrganization });
        this.feedbackData = importResponse.data && importResponse.data.result && importResponse.data.result.response
        this.showImportFeedbackModal = true;
      } catch (error) {
        let errorMsg = "We couldn’t import the patients from your CSV file.";

        if (error.response) {
          const { status, data } = error.response;
          if (status === 400) {
            if (data.detail && data.detail.message) {
              errorMsg = data.detail.message;
            } else if (typeof data.detail === 'string') {
              errorMsg = data.detail;
            }
          } else if (status >= 500) {
            errorMsg = "There was a problem with the server. Please try again later.";
          }
        }
        Swal.fire({
          title: "Error",
          text: errorMsg,
          type: "error",
          confirmButtonClass: "md-button md-info",
          buttonsStyling: false,
          timer: 3000
        })
      } finally {
        this.isImportingFile = false;
      }
    },
    async fetchOrganizations() {
      try {
        const url = `${process.env.VUE_APP_URL}/organisations/user/${sessionStorage.getItem("userID")}`;
        window.axios.get(url).then((res) => {
          res.data.sort((a, b) => a.OrgName.toLowerCase() > b.OrgName.toLowerCase() ? 1 : -1);
          this.orgIds = res.data;
          if (this.orgIds.length === 1) {
            this.orgIdControl = this.orgIds[0].OrgId;
            this.showOrganizationSelector = false;
          } else if (this.orgIds.length > 1) {
            this.showOrganizationSelector = true;
            this.orgIds.forEach((org) => {
              this.$set(this.orgNamesObject, org.OrgId, org.OrgName);
            });
          }
        }).catch((err) => {
          this.orgIds = [];
          this.isLoading = false;
        })
      } catch {
        this.orgIds = [];
        this.orgNamesObject = {};
        console.error('error while searching for orgIds')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#referral {
  width: 95%;
  margin-left: 20px;
  display: inline-block;
  vertical-align: top;
}


#patients_title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  #patient_csv_import {
    height: 38px;
  }

  #patients_import_modal {
    .md-table {
      height: max-content;
      max-height: 80vh;
    }
  }
}


.md-table {
  max-height: 67dvh;
  overflow: auto;
}

.md-table-cell {
  padding: 2px 8px;
  font-size: 14px;
}

.text-right ::v-deep .md-table-cell-container {
  display: flex;
  justify-content: flex-end;
}

.md-table ::v-deep .md-table-head:last-child {
  text-align: left;
}

.table-shopping {
  table>thead>tr>th {
    text-transform: uppercase;
  }

  table>thead>tr>.md-table-head .md-table-head-label {
    font-size: 0.75rem;

    b {
      display: block;
      margin-bottom: 3px;
    }
  }

  .td-name {
    font-size: 1em !important;
    line-height: 1.1;

    small {
      color: green;
      font-size: 0.77em;
    }
  }

  .td-name {
    min-width: 200px;
  }

  .td-phone {
    text-align: left;
    min-width: 150px;

    small {
      margin-right: 3px;
    }
  }

  .img-container {
    width: 120px;
    max-height: 160px;
    overflow: hidden;
    display: block;

    img {
      width: 100%;
    }
  }
}

.md-table-row:hover {
  background-color: #74b5ff !important;
  color: white;
}

.table-striped {
  tbody>tr:nth-of-type(odd) {
    background-color: #edf2f7;
  }

  tbody>tr:nth-of-type(even) {
    background-color: #ffffff;
  }
}

.table-selected {
  color: white;
  background-color: #005ac2 !important;

  a,
  small {
    color: white;
  }
}

.mmodal-container {
  max-width: 70vw;
  max-height: 75vh !important;
  overflow: auto;

  margin: 0px auto;
  position: relative;
  background-color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 27px 24px 0 rgba(0, 0, 0, 0.2), 0 40px 77px 0 rgba(0, 0, 0, 0.22);
  border-radius: 6px;
  border: none;
  z-index: 9999;
}

.selected-filter-item {
  color: #0041b3;
  font-weight: 500;
}

.md-table {
  cursor: pointer;
}

.dropdown {
  cursor: pointer;
}

.dropdown-menu {
  li {
    padding: 3px 3px 3px 5px;
  }

  li:hover {
    background-color: #005ac2 !important;
    color: #fff !important;
  }
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination-container span {
  margin: 0 15px;
}

.importing-file {
  clip-path: inset(0 1ch 0 0);
  animation: l 1s steps(4, jump-none) infinite;
}

@keyframes l {
  to {
    clip-path: inset(0)
  }
}
</style>
